import type { PieceType } from "../core/model";
import { drawPiece } from "./spriteAtlas";

const VIRTUAL_SIZE = 32;
const ERASE_FRAMES = 16;
const PIXELS_PER_FRAME = (VIRTUAL_SIZE * VIRTUAL_SIZE) / ERASE_FRAMES;
const orderCache = new Map<number, number[]>();
const imageCache = new Map<string, ImageData>();

function shuffleOrder(seed: number): number[] {
  const cached = orderCache.get(seed);
  if (cached) {
    return cached;
  }
  const order = Array.from({ length: VIRTUAL_SIZE * VIRTUAL_SIZE }, (_, index) => index);
  let state = seed >>> 0;
  for (let i = order.length - 1; i > 0; i -= 1) {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    const j = state % (i + 1);
    [order[i], order[j]] = [order[j], order[i]];
  }
  orderCache.set(seed, order);
  return order;
}

function pieceImage(type: PieceType, highContrast: boolean): ImageData {
  const key = `${type}:${highContrast ? 1 : 0}`;
  const cached = imageCache.get(key);
  if (cached) {
    return cached;
  }
  const canvas = document.createElement("canvas");
  canvas.width = VIRTUAL_SIZE;
  canvas.height = VIRTUAL_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context is unavailable");
  }
  drawPiece(ctx, type, 0, 0, VIRTUAL_SIZE, highContrast);
  const data = ctx.getImageData(0, 0, VIRTUAL_SIZE, VIRTUAL_SIZE);
  imageCache.set(key, data);
  return data;
}

export function drawEraseEffect(
  ctx: CanvasRenderingContext2D,
  type: PieceType,
  x: number,
  y: number,
  size: number,
  frame: number,
  seed: number,
  highContrast = false
): void {
  const image = pieceImage(type, highContrast);
  const order = shuffleOrder(seed);
  const removed = Math.min(VIRTUAL_SIZE * VIRTUAL_SIZE, Math.max(0, Math.floor(frame * PIXELS_PER_FRAME)));
  const pixelSize = size / VIRTUAL_SIZE;

  for (let index = removed; index < order.length; index += 1) {
    const pixel = order[index];
    const px = pixel % VIRTUAL_SIZE;
    const py = Math.floor(pixel / VIRTUAL_SIZE);
    const offset = (py * VIRTUAL_SIZE + px) * 4;
    const alpha = image.data[offset + 3];
    if (alpha === 0) {
      continue;
    }
    ctx.fillStyle = `rgba(${image.data[offset]}, ${image.data[offset + 1]}, ${image.data[offset + 2]}, ${alpha / 255})`;
    ctx.fillRect(x + px * pixelSize, y + py * pixelSize, Math.ceil(pixelSize), Math.ceil(pixelSize));
  }

  ctx.save();
  ctx.globalAlpha = Math.max(0, 1 - frame / ERASE_FRAMES) * 0.55;
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = Math.max(1, size * 0.035);
  ctx.strokeRect(x + size * 0.08, y + size * 0.08, size * 0.84, size * 0.84);
  ctx.restore();
}
